import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FolderKanban, Loader2, Search, Filter, Grid3x3, List, Clock, Edit2, Plus, Eye, EyeOff, CheckSquare, FileText, Bell, X, ChevronDown, ChevronUp, Check, Trash2, Sparkles, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import Avatar from './Avatar';
import ProjectModal from './ProjectModal';
import AddUpdateModal from './AddUpdateModal';
import AddHoursDialog from './AddHoursDialog';
import TaskModal from './TaskModal';
import AILeadModal from './AILeadModal';
import FlagIcon from './FlagIcon';
import { PaymentProgress } from './PaymentProgress';
import { formatEUR, getProjectValueEUR, fetchExchangeRates } from '../utils/currencyUtils';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { getDeadlineColor, formatDate } from '../utils/deadlineUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyProjects = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [projectUpdates, setProjectUpdates] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('projectViewMode') || 'table';
  });
  const [editingProject, setEditingProject] = useState(null);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showAddUpdateModal, setShowAddUpdateModal] = useState(false);
  const [updateProject, setUpdateProject] = useState(null);
  const [showHoursDialog, setShowHoursDialog] = useState(false);
  const [hoursProject, setHoursProject] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskProject, setTaskProject] = useState(null);
  const [expandedUpdate, setExpandedUpdate] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [userAvatars, setUserAvatars] = useState({});
  const [rates, setRates] = useState(null);
  useEffect(() => { if (token) fetchExchangeRates(API, token).then(setRates); }, [token]);
  const [showAILeadModal, setShowAILeadModal] = useState(false);
  const [aiExtractedData, setAiExtractedData] = useState(null);
  const [aiUploadedFile, setAiUploadedFile] = useState(null);
  
  // For Kanban view - track hidden statuses
  const [hiddenStatuses, setHiddenStatuses] = useState(() => {
    try {
      const saved = localStorage.getItem('hiddenProjectStatuses');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // For editing last update inline
  const [editingLastUpdate, setEditingLastUpdate] = useState(null); // projectId
  const [editingLastUpdateText, setEditingLastUpdateText] = useState('');

  // Save view preference
  useEffect(() => {
    localStorage.setItem('projectViewMode', viewMode);
  }, [viewMode]);

  // Save hidden statuses preference
  useEffect(() => {
    localStorage.setItem('hiddenProjectStatuses', JSON.stringify(hiddenStatuses));
  }, [hiddenStatuses]);

  // Get last update date and aging color
  const getLastUpdateInfo = (projectId) => {
    const updates = projectUpdates[projectId] || [];
    if (updates.length === 0) return { 
      date: 'Never', 
      text: 'No updates yet',
      author: '',
      time: '',
      color: 'text-red-600', 
      bgColor: 'bg-red-50',
      id: null
    };
    
    const lastUpdate = updates[0];
    // Parse date from dd/mm/yyyy format
    let lastDate;
    try {
      const [day, month, year] = lastUpdate.date.split('/');
      lastDate = new Date(year, month - 1, day);
    } catch (e) {
      // Fallback to direct parsing if split fails
      lastDate = new Date(lastUpdate.date);
    }
    const now = new Date();
    const daysDiff = Math.floor((now - lastDate) / (1000 * 60 * 60 * 24));
    
    let color, bgColor;
    if (daysDiff <= 7) {
      color = 'text-green-600';
      bgColor = 'bg-green-50';
    } else if (daysDiff <= 30) {
      color = 'text-yellow-600';
      bgColor = 'bg-yellow-50';
    } else {
      color = 'text-red-600';
      bgColor = 'bg-red-50';
    }
    
    return {
      date: lastDate.toLocaleDateString(),
      daysAgo: daysDiff,
      text: lastUpdate.text || 'No update text',
      author: lastUpdate.author || '',
      time: lastUpdate.time || '',
      color,
      bgColor,
      id: lastUpdate.id
    };
  };

  useEffect(() => {
    if (user && token) {
      fetchProjects();
      fetchStatusOptions();
    }
  }, [user, token]);

  useEffect(() => {
    filterProjects();
  }, [projects, searchTerm, selectedStatus]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/projects/user/${user.id}`, { 
        headers,
        params: { rows_per_page: 100 }
      });
      const projectsData = response.data.records || [];
      setProjects(projectsData);
      
      // Fetch last updates for projects
      fetchProjectUpdates(projectsData);
      
      // Fetch avatars for all users
      fetchUserAvatars(projectsData);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      if (error.response?.status === 403) {
        alert('Permission denied: You do not have access to view these projects.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUserAvatars = async (projectsList) => {
    try {
      // Collect all unique user IDs from projects
      const userIds = new Set();
      projectsList.forEach(project => {
        // Owner
        if (project.field_237_raw?.[0]?.id) {
          userIds.add(project.field_237_raw[0].id);
        }
        // Assistants
        if (project.field_57_raw) {
          project.field_57_raw.forEach(assistant => {
            if (assistant.id) userIds.add(assistant.id);
          });
        }
      });

      console.log('🔍 MyProjects - Requesting avatars for user IDs:', Array.from(userIds));

      if (userIds.size === 0) return;

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API}/users/avatars`,
        { user_ids: Array.from(userIds) },
        { headers }
      );
      
      console.log('✅ MyProjects - Received avatars:', response.data.avatars);
      console.log('📊 MyProjects - Avatar count:', Object.keys(response.data.avatars || {}).length, 'out of', userIds.size, 'requested');
      
      setUserAvatars(response.data.avatars || {});
    } catch (error) {
      console.error('Failed to fetch user avatars:', error);
    }
  };

  const fetchProjectUpdates = async (projectsList) => {
    // Fetch actual updates from the project_updates collection for accurate IDs
    const updatesMap = {};
    
    if (!projectsList || projectsList.length === 0) {
      setProjectUpdates({});
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch updates for all projects in parallel
      const updatePromises = projectsList.map(async (project) => {
        try {
          const response = await axios.get(`${API}/projects/${project.id}/updates`, { headers });
          // The API returns {updates: [...]} not just an array
          const updatesData = response.data?.updates || response.data || [];
          return { projectId: project.id, updates: updatesData };
        } catch (error) {
          console.error(`Failed to fetch updates for project ${project.id}:`, error);
          return { projectId: project.id, updates: [] };
        }
      });
      
      const results = await Promise.all(updatePromises);
      
      results.forEach(({ projectId, updates }) => {
        updatesMap[projectId] = updates;
      });
      
      setProjectUpdates(updatesMap);
    } catch (error) {
      console.error('Error fetching project updates:', error);
      setProjectUpdates({});
    }
  };

  const fetchStatusOptions = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/reference-data/project-status`, { headers });
      const statusList = response.data?.records || response.data || [];
      
      // Sort by order field if it exists, otherwise by name
      const sorted = statusList.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return (a.field_84_raw || '').localeCompare(b.field_84_raw || '');
      });
      
      setStatusOptions(sorted);
    } catch (error) {
      console.error('Failed to fetch status options:', error);
      setStatusOptions([]);
    }
  };

  const filterProjects = () => {
    let result = projects;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(project => 
        (project.field_5_raw || '').toLowerCase().includes(term) ||
        (project.field_2_raw || '').toLowerCase().includes(term) ||
        (project.field_117_raw || '').toLowerCase().includes(term)
      );
    }

    if (selectedStatus !== 'all') {
      result = result.filter(project => {
        const status = project.field_85_raw?.[0]?.identifier || '';
        return status.toLowerCase().includes(selectedStatus.toLowerCase());
      });
    }

    setFilteredProjects(result);
  };

  const updateProjectStatus = async (projectId, newStatusId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/projects/${projectId}`, {
        field_85: [newStatusId]
      }, { headers });

      const updatedStatus = statusOptions.find(s => s.id === newStatusId);
      setProjects(prevProjects => 
        prevProjects.map(p => 
          p.id === projectId 
            ? { ...p, field_85_raw: [{ id: newStatusId, identifier: updatedStatus?.field_84_raw }] }
            : p
        )
      );
    } catch (error) {
      console.error('Failed to update project status:', error);
    }
  };

  // Group projects by status
  const groupByStatus = () => {
    const grouped = {};
    
    // Initialize grouped object with statusOptions order to maintain sorting
    statusOptions.forEach(status => {
      const statusName = status.field_84_raw;
      grouped[statusName] = [];
    });
    
    // Group projects into the ordered structure
    filteredProjects.forEach(project => {
      const status = project.field_85_raw?.[0]?.identifier || 'Unknown';
      if (!grouped[status]) {
        // If status doesn't exist in statusOptions, add it at the end
        grouped[status] = [];
      }
      grouped[status].push(project);
    });

    // Filter out empty statuses (hide statuses with 0 projects)
    const filtered = {};
    Object.entries(grouped).forEach(([status, projects]) => {
      if (projects.length > 0) {
        filtered[status] = projects;
      }
    });

    return filtered;
  };

  // For Kanban view - filter out hidden statuses and empty statuses, maintain order
  const getVisibleGroupedProjects = () => {
    const grouped = {};
    
    // Initialize grouped object with statusOptions order
    statusOptions.forEach(status => {
      const statusName = status.field_84_raw;
      if (!hiddenStatuses.includes(statusName)) {
        grouped[statusName] = [];
      }
    });
    
    // Group projects into the ordered structure
    filteredProjects.forEach(project => {
      const status = project.field_85_raw?.[0]?.identifier || 'No Status';
      if (grouped[status]) {
        grouped[status].push(project);
      }
    });
    
    // Filter out empty statuses (hide statuses with 0 projects)
    const filtered = {};
    Object.entries(grouped).forEach(([status, projects]) => {
      if (projects.length > 0) {
        filtered[status] = projects;
      }
    });
    
    return filtered;
  };

  // Handle drag end in Kanban view
  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;

    console.log('🔄 Drag ended:', {
      source: source.droppableId,
      destination: destination?.droppableId,
      projectId: draggableId
    });

    if (!destination || 
        (source.droppableId === destination.droppableId && 
         source.index === destination.index)) {
      console.log('❌ No valid destination or same position');
      return;
    }

    const project = projects.find(p => p.id === draggableId);
    const newStatus = destination.droppableId.replace('status-', '');
    const oldStatus = source.droppableId.replace('status-', '');

    console.log('📊 Status change:', {
      from: oldStatus,
      to: newStatus,
      projectName: project?.field_5_raw
    });

    console.log('📋 Available status options:', statusOptions.map(s => ({
      id: s.id,
      name: s.field_84_raw
    })));

    const newStatusOption = statusOptions.find(s => s.field_84_raw === newStatus);
    
    if (!newStatusOption) {
      console.error('❌ Status option not found for:', newStatus);
      console.error('Available statuses:', statusOptions.map(s => s.field_84_raw));
      alert(`Error: Could not find status "${newStatus}". Please refresh the page and try again.`);
      return;
    }

    console.log('✅ Found status option:', {
      id: newStatusOption.id,
      name: newStatusOption.field_84_raw
    });

    // Optimistically update UI with both id and identifier
    setProjects(prevProjects => 
      prevProjects.map(p => {
        if (p.id === draggableId) {
          return { 
            ...p, 
            field_85: [newStatusOption.id],
            field_85_raw: [{ id: newStatusOption.id, identifier: newStatus }] 
          };
        }
        return p;
      })
    );

    try {
      console.log('📤 Sending update to API:', {
        projectId: draggableId,
        statusId: newStatusOption.id,
        statusName: newStatus
      });

      const response = await axios.put(`${API}/projects/${draggableId}`, {
        field_85: [newStatusOption.id],
        field_85_raw: [{ 
          id: newStatusOption.id, 
          identifier: newStatus,
          field_84_raw: newStatus
        }]
      }, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      
      console.log('✅ API Response:', response.data);
      console.log(`✅ Project ${draggableId} status updated to ${newStatus}`);
      
      // Dispatch event to notify other components about status change
      window.dispatchEvent(new CustomEvent('projectUpdateCreated', {
        detail: { projectId: draggableId, type: 'status_change' }
      }));
      
      // Refetch to ensure we have the latest data
      await fetchProjects();
      console.log('✅ Projects refetched');
    } catch (error) {
      console.error('❌ Failed to update project status:', error);
      console.error('Error details:', error.response?.data);
      
      if (error.response?.status === 403) {
        alert('Permission denied: You do not have permission to update this project. Only project owners, assistants, and administrators can make changes.');
      } else {
        alert(`Failed to update project status: ${error.response?.data?.detail || error.message}`);
      }
      
      // Revert
      setProjects(prevProjects => 
        prevProjects.map(p => {
          if (p.id === draggableId) {
            return { 
              ...p, 
              field_85_raw: [{ identifier: oldStatus }] 
            };
          }
          return p;
        })
      );
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Lead': 'bg-purple-100 text-purple-700',
      'Submitted (Bid)': 'bg-yellow-100 text-yellow-700',
      'Bid Preparation': 'bg-orange-100 text-orange-700',
      'Ongoing': 'bg-blue-100 text-blue-700',
      'Final Payment': 'bg-green-100 text-green-700',
      'Closed (Won)': 'bg-green-100 text-green-700',
      'Closed (Lost)': 'bg-red-100 text-red-700',
      'Closed (Cancelled)': 'bg-gray-100 text-gray-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };


  const handleStatusChange = async (project, newStatusId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Find the full status object
      const statusOption = statusOptions.find(s => s.id === newStatusId);
      if (!statusOption) {
        console.error('Status option not found for ID:', newStatusId);
        alert('Failed to update project status: Status not found');
        return;
      }
      
      // Send both field_85 and field_85_raw with complete status object
      await axios.put(`${API}/projects/${project.id}`, {
        field_85: [statusOption.id],
        field_85_raw: [{ 
          id: statusOption.id, 
          identifier: statusOption.field_84_raw || statusOption.field_84,
          field_84_raw: statusOption.field_84_raw || statusOption.field_84
        }]
      }, { headers });
      
      // Dispatch event to notify other components about status change
      window.dispatchEvent(new CustomEvent('projectUpdateCreated', {
        detail: { projectId: project.id, type: 'status_change' }
      }));
      
      // Refresh projects
      fetchProjects();
    } catch (error) {
      console.error('Failed to update status:', error);
      if (error.response?.status === 403) {
        alert('Permission denied: You do not have permission to update this project. Only project owners, assistants, and administrators can make changes.');
      } else {
        alert('Failed to update project status: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const QuickActions = ({ project }) => {
    return (
      <div className="flex gap-1">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setEditingProject(project);
            setShowProjectModal(true);
          }}
          className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition"
          title="Edit project"
        >
          <Edit2 className="w-4 h-4" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setUpdateProject(project);
            setShowAddUpdateModal(true);
          }}
          className="p-1.5 text-green-600 hover:bg-green-50 rounded transition"
          title="Add Update"
        >
          <Plus className="w-4 h-4" />
        </button>
        <button
          title="Register Hours"
          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition"
          onClick={(e) => {
            e.stopPropagation();
            setHoursProject(project);
            setShowHoursDialog(true);
          }}
        >
          <Clock className="w-4 h-4" />
        </button>
        <button
          title="Add Task"
          className="p-1.5 text-green-600 hover:bg-green-50 rounded transition"
          onClick={(e) => {
            e.stopPropagation();
            setTaskProject(project);
            setShowTaskModal(true);
          }}
        >
          <CheckSquare className="w-4 h-4" />
        </button>
        <button
          title="Add Note"
          className="p-1.5 text-purple-600 hover:bg-purple-50 rounded transition"
          onClick={(e) => e.stopPropagation()}
        >
          <FileText className="w-4 h-4" />
        </button>
        <button
          title="Add Reminder"
          className="p-1.5 text-orange-600 hover:bg-orange-50 rounded transition"
          onClick={(e) => e.stopPropagation()}
        >
          <Bell className="w-4 h-4" />
        </button>
      </div>
    );
  };

  const UserAvatars = ({ project }) => {
    const owner = project.field_237_raw?.[0]; // Owner field
    const assistants = project.field_57_raw || []; // Assistants field
    const allUsersWithDuplicates = owner ? [owner, ...assistants] : assistants;
    
    // Remove duplicate users by ID
    const allUsers = allUsersWithDuplicates.filter((user, index, self) =>
      index === self.findIndex(u => u.id === user.id)
    );

    const getAvatarUrl = (user) => {
      // Get avatar from fetched avatars map
      return userAvatars[user.id] || null;
    };

    if (allUsers.length === 0) return <span className="text-xs text-gray-500">No team</span>;

    return (
      <div className="flex -space-x-2">
        {allUsers.slice(0, 3).map((user, idx) => {
          const avatarUrl = getAvatarUrl(user);
          
          return (
            <div key={user.id} title={user.identifier} className="-ml-1 first:ml-0">
              <Avatar
                userId={user.id}
                userName={user.identifier}
                avatarUrl={avatarUrl}
                size="sm"
              />
            </div>
          );
        })}
        {allUsers.length > 3 && (
          <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-gray-700 text-xs font-medium border-2 border-white">
            +{allUsers.length - 3}
          </div>
        )}
      </div>
    );
  };

  const StatusDropdown = ({ project }) => {
    const currentStatus = project.field_85_raw?.[0];
    
    return (
      <select
        value={currentStatus?.id || ''}
        onChange={(e) => updateProjectStatus(project.id, e.target.value)}
        className={`text-xs font-medium px-2 py-1 rounded-full border-0 focus:ring-2 focus:ring-blue-500 cursor-pointer ${getStatusColor(currentStatus?.identifier)}`}
        onClick={(e) => e.stopPropagation()}
      >
        {statusOptions.map(status => (
          <option key={status.id} value={status.id} className="text-gray-900 bg-white">
            {status.field_84_raw}
          </option>
        ))}
      </select>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const groupedProjects = viewMode === 'kanban' ? getVisibleGroupedProjects() : groupByStatus();

  return (
    <div className="space-y-6">
      {showProjectModal && (
        <ProjectModal
          project={editingProject}
          aiExtractedData={aiExtractedData}
          aiUploadedFile={aiUploadedFile}
          onClose={() => {
            setShowProjectModal(false);
            setEditingProject(null);
            setAiExtractedData(null); // Clear AI data
            setAiUploadedFile(null); // Clear uploaded file
          }}
          onSave={() => {
            fetchProjects();
            setAiExtractedData(null); // Clear AI data
            setAiUploadedFile(null); // Clear uploaded file
          }}
        />
      )}

      {showAddUpdateModal && (
        <AddUpdateModal
          project={updateProject}
          onClose={() => {
            setShowAddUpdateModal(false);
            setUpdateProject(null);
          }}
          onSave={fetchProjects}
        />
      )}

      {/* AI Lead Modal */}
      <AILeadModal
        isOpen={showAILeadModal}
        onClose={() => setShowAILeadModal(false)}
        onLeadCreated={(data) => {
          setShowAILeadModal(false);
          // Set the AI extracted data and uploaded file
          setAiExtractedData(data.extractedData);
          setAiUploadedFile(data.uploadedFile);
          setEditingProject(null); // Creating new project
          setShowProjectModal(true);
        }}
      />

      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">My Projects</h1>
            <p className="text-gray-600 mt-1">
              {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''} 
              {searchTerm || selectedStatus !== 'all' ? ` (filtered from ${projects.length} total)` : ''}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* AI Lead Button */}
            <button
              onClick={() => setShowAILeadModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition shadow-sm"
              title="Generate lead from tender document using AI"
            >
              <Sparkles className="w-4 h-4" />
              <span className="hidden sm:inline">AI Add Lead</span>
            </button>
            
            {/* Create Project Button */}
            <button
              onClick={() => {
                setEditingProject(null);
                setShowProjectModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-sm"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Create Project</span>
            </button>

            {/* View Mode Switcher - Only show when not in Kanban */}
            {viewMode !== 'kanban' && (
              <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                    viewMode === 'table' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                  }`}
                  title="Table View"
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('card')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                    viewMode === 'card' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                  }`}
                  title="Card View"
                >
                  <Grid3x3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('kanban')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                    viewMode === 'kanban' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                  }`}
                  title="Kanban View"
                >
                  <FolderKanban className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Search and Filter */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none text-gray-900"
            >
              <option value="all" className="text-gray-900 bg-white">All Statuses</option>
              {statusOptions.map(status => (
                <option key={status.id} value={status.field_84_raw} className="text-gray-900 bg-white">
                  {status.field_84_raw}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Status visibility toggles - Show in both table and kanban views */}
        {statusOptions.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-sm text-gray-600 flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Toggle columns:
            </span>
            {statusOptions.map(status => {
              const statusName = status.field_84_raw;
              const isHidden = hiddenStatuses.includes(statusName);
              return (
                <button
                  key={status.id}
                  onClick={() => {
                    setHiddenStatuses(prev => {
                      if (prev.includes(statusName)) {
                        return prev.filter(s => s !== statusName);
                      } else {
                        return [...prev, statusName];
                      }
                    });
                  }}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                    isHidden 
                      ? 'bg-gray-200 text-gray-500 line-through' 
                      : getStatusColor(statusName)
                  }`}
                >
                  {isHidden ? <EyeOff className="w-3 h-3 inline mr-1" /> : <Eye className="w-3 h-3 inline mr-1" />}
                  {statusName}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Projects Display */}
      {Object.keys(groupedProjects).length > 0 ? (
        viewMode === 'kanban' ? (
          /* KANBAN VIEW */
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="bg-white rounded-lg shadow overflow-hidden">
              {/* Kanban header with view switcher - STICKY */}
              <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Kanban Board</h2>
                  <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('table')}
                      className="px-3 py-1.5 rounded-md text-sm font-medium text-gray-600 hover:text-gray-900 transition"
                      title="Table View"
                    >
                      <List className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('card')}
                      className="px-3 py-1.5 rounded-md text-sm font-medium text-gray-600 hover:text-gray-900 transition"
                      title="Card View"
                    >
                      <Grid3x3 className="w-4 h-4" />
                    </button>
                    <button
                      className="px-3 py-1.5 rounded-md text-sm font-medium bg-white text-gray-900 shadow-sm"
                      title="Kanban View (Current)"
                    >
                      <FolderKanban className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Scrollable Kanban columns */}
              <div className="p-6 overflow-x-auto">
                <div className="flex gap-4 pb-4 min-w-min">
                  {Object.entries(groupedProjects).map(([status, statusProjects]) => (
                    <div key={status} className="flex-shrink-0 w-80 bg-gray-50 rounded-lg">
                      <div className="p-4 border-b border-gray-200 bg-gray-100 rounded-t-lg">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-gray-900">{status}</h3>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600 bg-white px-2 py-0.5 rounded-full">
                              {statusProjects.length}
                            </span>
                            <button
                              onClick={() => {
                                setHiddenStatuses(prev => [...prev, status]);
                              }}
                              className="p-1 hover:bg-gray-200 rounded transition"
                              title="Hide this column"
                          >
                            <X className="w-4 h-4 text-gray-600" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <Droppable droppableId={`status-${status}`}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`p-3 space-y-3 min-h-[200px] max-h-[600px] overflow-y-auto ${
                            snapshot.isDraggingOver ? 'bg-blue-50' : ''
                          }`}
                        >
                          {statusProjects.map((project, index) => (
                            <Draggable
                              key={project.id}
                              draggableId={project.id}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`bg-white border rounded-lg transition ${
                                    snapshot.isDragging
                                      ? 'border-blue-500 shadow-lg rotate-2'
                                      : 'border-gray-200 hover:shadow-md hover:border-blue-300'
                                  }`}
                                >
                                  <div className="flex items-start gap-2 p-3">
                                    <div 
                                      {...provided.dragHandleProps}
                                      className="text-gray-400 cursor-grab active:cursor-grabbing flex-shrink-0 pt-1"
                                      title="Drag to move"
                                    >
                                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"></path>
                                      </svg>
                                    </div>
                                    <FlagIcon countryCode={project.field_117_raw} />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs text-gray-500 mb-1">{project.field_2_raw || 'No ID'}</p>
                                      <h4 className="text-sm font-semibold text-gray-900 line-clamp-2 cursor-pointer hover:text-blue-600"
                                          onClick={() => navigate(`/projects/${project.id}`)}>
                                        {project.field_5_raw || 'Untitled Project'}
                                      </h4>
                                    </div>
                                  </div>
                                  <div className="px-3 pb-3">
                                    <div className="space-y-1 text-xs text-gray-600 mb-2">
                                      {project.field_10_raw && (
                                        <div className="flex justify-between">
                                          <span>Value:</span>
                                          <span className="font-medium">{getProjectValueEUR(project, rates) > 0 ? formatEUR(getProjectValueEUR(project, rates)) : '-'}</span>
                                        </div>
                                      )}
                                      {project.field_9_raw && (
                                        <div className="flex justify-between">
                                          <span>Deadline:</span>
                                          <span className={getDeadlineColor(project.field_9)}>{formatDate(project.field_9)}</span>
                                        </div>
                                      )}
                                    </div>
                                    {project.payment_milestones?.length > 0 && (
                                      <PaymentProgress milestones={project.payment_milestones} variant="bar" className="mb-2" />
                                    )}
                                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                                      <UserAvatars project={project} />
                                      <QuickActions project={project} />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                ))}
                </div>
              </div>
            </div>
          </DragDropContext>
        ) : (
          /* TABLE & CARD VIEWS */
          Object.entries(groupedProjects).map(([status, statusProjects]) => (
            <div key={status} className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">{status}</h2>
                  <span className="text-sm text-gray-600">
                    {statusProjects.length} project{statusProjects.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              <div className="p-6">
                {statusProjects.length > 0 ? (
                  viewMode === 'card' ? (
                    /* CARD VIEW */
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {statusProjects.map(project => {
                        const updateInfo = getLastUpdateInfo(project.id);
                        return (
                          <div
                            key={project.id}
                            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition"
                          >
                            <div className="flex items-start gap-3 mb-3">
                              <FlagIcon countryCode={project.field_117_raw} />
                              <div 
                                className="flex-1 min-w-0 cursor-pointer"
                                onClick={() => navigate(`/projects/${project.id}`)}
                              >
                                <p className="text-xs text-gray-500">{project.field_2_raw || 'No ID'}</p>
                                <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">
                                  {project.field_5_raw || 'Untitled Project'}
                                </h3>
                              </div>
                              {/* Action buttons */}
                              <div className="flex gap-1 flex-shrink-0">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingProject(project);
                                    setShowProjectModal(true);
                                  }}
                                  className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded"
                                  title="Edit Project"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setUpdateProject(project);
                                    setShowAddUpdateModal(true);
                                  }}
                                  className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded"
                                  title="Add Update"
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                            <div className="space-y-2 text-xs text-gray-600 mb-3">
                              {project.field_10_raw && (
                                <div className="flex justify-between">
                                  <span>Value:</span>
                                  <span className="font-medium">{project.field_10 || '-'}</span>
                                </div>
                              )}
                              {project.field_9_raw && (
                                <div className="flex justify-between">
                                  <span>Deadline:</span>
                                  <span className={getDeadlineColor(project.field_9)}>{formatDate(project.field_9)}</span>
                                </div>
                              )}
                              <div className="space-y-1">
                                <div 
                                  className="flex justify-between items-center cursor-pointer hover:bg-gray-50 -mx-2 px-2 py-1 rounded transition"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingProject(project);
                                    setShowProjectModal(true);
                                  }}
                                  title="Click to view/edit project"
                                >
                                  <span>Last Update:</span>
                                  <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${updateInfo.bgColor} ${updateInfo.color}`}>
                                    <Clock className="w-2.5 h-2.5" />
                                    <span>{updateInfo.date}</span>
                                  </div>
                                </div>
                                {updateInfo.text && updateInfo.text !== 'No update text' && (
                                  <p 
                                    className="text-xs text-gray-600 line-clamp-2 italic cursor-pointer hover:text-gray-900"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingProject(project);
                                      setShowProjectModal(true);
                                    }}
                                    title="Click to view/edit project"
                                  >
                                    "{updateInfo.text}"
                                  </p>
                                )}
                              </div>
                            </div>
                            {project.payment_milestones?.length > 0 && (
                              <PaymentProgress milestones={project.payment_milestones} variant="bar" className="mb-3" />
                            )}
                            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                              <StatusDropdown project={project} />
                              <UserAvatars project={project} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    /* TABLE VIEW */
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deadline</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Team</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {statusProjects.map((project) => {
                            const updateInfo = getLastUpdateInfo(project.id);
                            const hasUpdate = updateInfo && updateInfo.date !== 'Never';
                            
                            return (
                              <React.Fragment key={project.id}>
                                {/* Main Project Row */}
                                <tr className="hover:bg-gray-50">
                                  {/* Image Column */}
                                  <td className="px-6 py-4">
                                    {project.featured_image ? (
                                      <img
                                        src={project.featured_image}
                                        alt={project.field_5_raw || 'Project'}
                                        className="w-12 h-12 object-cover rounded border border-gray-200"
                                      />
                                    ) : (
                                      <button
                                        onClick={() => {
                                          setEditingProject(project);
                                          setShowProjectModal(true);
                                        }}
                                        className="w-12 h-12 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded border border-gray-300 border-dashed transition"
                                        title="Add project image"
                                      >
                                        <ImageIcon className="w-5 h-5 text-gray-400" />
                                      </button>
                                    )}
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-3">
                                      <FlagIcon countryCode={project.field_117_raw} />
                                      <div>
                                        <p className="text-xs text-gray-500">{project.field_2_raw || 'No ID'}</p>
                                        <button
                                          onClick={() => {
                                            setEditingProject(project);
                                            setShowProjectModal(true);
                                          }}
                                          className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline text-left"
                                        >
                                          {project.field_5_raw || 'Untitled Project'}
                                        </button>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                                    <select
                                      value={project.field_85_raw?.[0]?.id || ''}
                                      onChange={(e) => handleStatusChange(project, e.target.value)}
                                      className="text-xs px-2 py-1 border border-gray-300 rounded bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                                    >
                                      {statusOptions.map(status => (
                                        <option key={status.id} value={status.id} className="text-gray-900 bg-white">
                                          {status.field_84_raw || status.field_84 || 'Unknown'}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-900">
                                    {getProjectValueEUR(project, rates) > 0 ? formatEUR(getProjectValueEUR(project, rates)) : '-'}
                                  </td>
                                  <td className="px-6 py-4">
                                    {project.payment_milestones?.length > 0 ? (
                                      <div className="w-28">
                                        <PaymentProgress milestones={project.payment_milestones} variant="bar" />
                                      </div>
                                    ) : (
                                      <span className="text-xs text-gray-400">-</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 text-sm">
                                    <span className={getDeadlineColor(project.field_9)}>
                                      {formatDate(project.field_9)}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <UserAvatars project={project} />
                                  </td>
                                  <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                                    <QuickActions project={project} />
                                  </td>
                                </tr>
                                
                                {/* Update Row - Spans all columns */}
                                {hasUpdate && (
                                  <tr className="bg-gray-50">
                                    <td colSpan={8} className="px-6 py-3" onClick={(e) => e.stopPropagation()}>
                                      <div className="flex items-start gap-2 w-full group hover:bg-gray-50 -mx-2 px-2 py-1 rounded transition-colors">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setExpandedUpdate(expandedUpdate === project.id ? null : project.id);
                                          }}
                                          className="flex items-start gap-2 text-left p-2 rounded -ml-2 flex-1"
                                        >
                                          <Clock className={`w-3 h-3 flex-shrink-0 mt-0.5 ${updateInfo.color}`} />
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                              <span className={`text-xs font-medium ${updateInfo.color}`}>
                                                Last Update: {updateInfo.date}
                                              </span>
                                              {updateInfo.daysAgo !== undefined && (
                                                <span className={`text-[10px] ${updateInfo.color} opacity-75`}>
                                                  ({updateInfo.daysAgo}d ago)
                                                </span>
                                              )}
                                              {updateInfo.author && (
                                                <span className="text-xs text-gray-500">by {updateInfo.author}</span>
                                              )}
                                            </div>
                                            {editingLastUpdate === project.id ? (
                                              <textarea
                                                value={editingLastUpdateText}
                                                onChange={(e) => setEditingLastUpdateText(e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                                className="w-full text-xs p-2 mt-1 border border-gray-300 rounded bg-white focus:ring-2 focus:ring-blue-500"
                                                rows={2}
                                                autoFocus
                                              />
                                            ) : (
                                              updateInfo.text && updateInfo.text !== 'No update text' && (
                                                <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                                                  {updateInfo.text}
                                                </p>
                                              )
                                            )}
                                          </div>
                                          {expandedUpdate === project.id ? (
                                            <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                          ) : (
                                            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                          )}
                                        </button>
                                        {updateInfo.id && (
                                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity pt-2 flex-shrink-0">
                                            {editingLastUpdate === project.id ? (
                                              <>
                                                <button
                                                  onClick={async (e) => {
                                                    e.stopPropagation();
                                                    try {
                                                      const response = await fetch(`${API}/projects/updates/${updateInfo.id}`, {
                                                        method: 'PUT',
                                                        headers: {
                                                          'Authorization': `Bearer ${token}`,
                                                          'Content-Type': 'application/json'
                                                        },
                                                        body: JSON.stringify({ update_text: editingLastUpdateText })
                                                      });
                                                      if (response.ok) {
                                                        await fetchProjectUpdates();
                                                        setEditingLastUpdate(null);
                                                      }
                                                    } catch (error) {
                                                      console.error('Error saving update:', error);
                                                    }
                                                  }}
                                                  className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded"
                                                  title="Save"
                                                >
                                                  <Check className="w-3 h-3" />
                                                </button>
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEditingLastUpdate(null);
                                                  }}
                                                  className="p-1 text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded"
                                                  title="Cancel"
                                                >
                                                  <X className="w-3 h-3" />
                                                </button>
                                              </>
                                            ) : (
                                              <>
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEditingLastUpdate(project.id);
                                                    setEditingLastUpdateText(updateInfo.text);
                                                  }}
                                                  className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded"
                                                  title="Edit"
                                                >
                                                  <Edit2 className="w-3 h-3" />
                                                </button>
                                                <button
                                                  onClick={async (e) => {
                                                    e.stopPropagation();
                                                    if (!window.confirm('Are you sure you want to delete this update?')) return;
                                                    try {
                                                      const response = await fetch(`${API}/projects/updates/${updateInfo.id}`, {
                                                        method: 'DELETE',
                                                        headers: { 'Authorization': `Bearer ${token}` }
                                                      });
                                                      if (response.ok) {
                                                        await fetchProjectUpdates();
                                                      }
                                                    } catch (error) {
                                                      console.error('Error deleting update:', error);
                                                    }
                                                  }}
                                                  className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
                                                  title="Delete"
                                                >
                                                  <Trash2 className="w-3 h-3" />
                                                </button>
                                              </>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                      
                                      {/* Update History - Expandable */}
                                      {expandedUpdate === project.id && (
                                        <UpdateHistorySection 
                                          project={project}
                                          onUpdateChange={(newLastUpdate) => {
                                            // Update just this project's lastUpdate without full refresh
                                            setProjects(prevProjects => 
                                              prevProjects.map(p => 
                                                p.id === project.id ? {...p, lastUpdate: newLastUpdate} : p
                                              )
                                            );
                                          }}
                                        />
                                      )}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )
                ) : (
                  <p className="text-center text-gray-500 py-8">No projects in this status</p>
                )}
              </div>
            </div>
          ))
        )
      ) : (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500">No projects found</p>
        </div>
      )}

      {/* Add Hours Dialog */}
      {showHoursDialog && (
        <AddHoursDialog
          isOpen={showHoursDialog}
          onClose={() => {
            setShowHoursDialog(false);
            setHoursProject(null);
          }}
          project={hoursProject}
          onSuccess={() => {
            setShowHoursDialog(false);
            setHoursProject(null);
          }}
          onCreateTask={(project) => {
            setTaskProject(project);
            setShowTaskModal(true);
          }}
        />
      )}

      {/* Task Modal */}
      {showTaskModal && (
        <TaskModal
          isOpen={showTaskModal}
          onClose={() => {
            setShowTaskModal(false);
            setTaskProject(null);
          }}
          task={null}
          onSuccess={() => {
            setShowTaskModal(false);
            setTaskProject(null);
            fetchProjects(); // Refresh projects after adding task
          }}
          preSelectedProject={taskProject}
        />
      )}
    </div>
  );
};

// Update History Section Component with Edit/Delete
const UpdateHistorySection = ({ project, onUpdateChange }) => {
  const [history, setHistory] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editingIndex, setEditingIndex] = React.useState(null);
  const [editText, setEditText] = React.useState('');

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${BACKEND_URL}/api/projects/${project.id}/updates`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const updates = data.updates || [];
        setHistory(updates);
        
        // Update the lastUpdate in parent component (newest update is first)
        if (onUpdateChange && updates.length > 0) {
          onUpdateChange(updates[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching update history:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchHistory();
  }, [project.id]);

  const startEdit = (index, currentText) => {
    setEditingIndex(index);
    setEditText(currentText);
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditText('');
  };

  const saveEdit = async (updateId) => {
    try {
      const token = localStorage.getItem('token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${BACKEND_URL}/api/projects/updates/${updateId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          update_text: editText
        })
      });

      if (response.ok) {
        await fetchHistory();
        cancelEdit();
      } else {
        alert('Failed to update');
      }
    } catch (error) {
      console.error('Error saving update:', error);
      alert('Failed to save update');
    }
  };

  const deleteUpdate = async (updateId) => {
    if (!window.confirm('Are you sure you want to delete this update?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${BACKEND_URL}/api/projects/updates/${updateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchHistory();
      } else {
        alert('Failed to delete');
      }
    } catch (error) {
      console.error('Error deleting update:', error);
      alert('Failed to delete update');
    }
  };

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-4 mt-2">
      <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Update History</h5>
      {loading ? (
        <div className="text-center py-6 text-gray-500 dark:text-gray-400">
          <p className="text-sm">Loading history...</p>
        </div>
      ) : history.length > 0 ? (
        <div className="space-y-3">
          {history.map((update, index) => (
            <div key={index} className="flex gap-3 pb-3 border-b border-gray-200 dark:border-gray-600 last:border-0 group">
              <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-blue-600"></div>
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    {update.date && <span className="font-medium">{update.date}</span>}
                    {update.time && <span>at {update.time}</span>}
                    {update.author && (
                      <>
                        <span>•</span>
                        <span>{update.author}</span>
                      </>
                    )}
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {editingIndex === index ? (
                      <>
                        <button
                          onClick={() => saveEdit(update.id)}
                          className="p-1 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                          title="Save"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="p-1 text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                          title="Cancel"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEdit(index, update.text)}
                          className="p-1 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteUpdate(update.id)}
                          className="p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {editingIndex === index ? (
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="w-full text-sm p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    autoFocus
                  />
                ) : (
                  <p className="text-sm text-gray-700 dark:text-gray-300">{update.text}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6 text-gray-500 dark:text-gray-400">
          <p className="text-sm">No update history yet.</p>
          <p className="text-xs mt-1">Updates will appear here after you add them.</p>
        </div>
      )}
    </div>
  );
};

export default MyProjects;
